
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, ExternalLink, Clock, ChevronDown, ChevronRight } from "lucide-react";
import { Tutorial, TutorialProgress } from "@/types/tutorial";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface TutorialViewerProps {
  tutorial: Tutorial;
  onBack: () => void;
}

// Function to create safe HTML content for descriptions
const createSafeHTML = (htmlContent: string) => {
  return { __html: htmlContent };
};

const TutorialViewer: React.FC<TutorialViewerProps> = ({ tutorial, onBack }) => {
  const [progress, setProgress] = useState<TutorialProgress>({
    tutorialId: tutorial.id,
    completedSteps: [],
    lastAccessedAt: new Date().toISOString()
  });

  const [sectionStates, setSectionStates] = useState<Record<string, boolean>>({});

  // Sort BOM items by categories
  const sortedBom = React.useMemo(() => {
    if (!tutorial.bom || tutorial.bom.length === 0) return [];
    
    const bomWithCategories = tutorial.bom.map(item => ({
      ...item,
      sortKey: item.categories && item.categories.length > 0 ? item.categories[0] : 'Uncategorized'
    }));
    
    return bomWithCategories.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  }, [tutorial.bom]);

  // Load progress from localStorage
  useEffect(() => {
    const savedProgress = localStorage.getItem(`tutorial-progress-${tutorial.id}`);
    if (savedProgress) {
      const parsed = JSON.parse(savedProgress);
      setProgress(parsed);
      
      // Initialize section states based on progress
      const initialStates: Record<string, boolean> = {};
      tutorial.sections.forEach((section, index) => {
        const allStepsCompleted = section.steps.every(step => 
          parsed.completedSteps.includes(step.id)
        );
        // First section or current section should be open
        initialStates[section.id] = index === 0 || !allStepsCompleted;
      });
      setSectionStates(initialStates);
    } else {
      // Initialize with first section open
      const initialStates: Record<string, boolean> = {};
      tutorial.sections.forEach((section, index) => {
        initialStates[section.id] = index === 0;
      });
      setSectionStates(initialStates);
    }
  }, [tutorial.id, tutorial.sections]);

  // Save progress to localStorage
  useEffect(() => {
    localStorage.setItem(`tutorial-progress-${tutorial.id}`, JSON.stringify(progress));
  }, [progress, tutorial.id]);

  const toggleStep = (stepId: string) => {
    const newCompletedSteps = progress.completedSteps.includes(stepId)
      ? progress.completedSteps.filter(id => id !== stepId)
      : [...progress.completedSteps, stepId];

    setProgress(prev => ({
      ...prev,
      completedSteps: newCompletedSteps,
      lastAccessedAt: new Date().toISOString()
    }));

    // Check if section should auto-collapse/expand
    const section = tutorial.sections.find(s => 
      s.steps.some(step => step.id === stepId)
    );
    
    if (section) {
      const allStepsCompleted = section.steps.every(step => 
        newCompletedSteps.includes(step.id)
      );
      
      if (allStepsCompleted) {
        // Collapse this section and expand next
        setSectionStates(prev => {
          const newStates = { ...prev };
          newStates[section.id] = false;
          
          const sectionIndex = tutorial.sections.findIndex(s => s.id === section.id);
          const nextSection = tutorial.sections[sectionIndex + 1];
          if (nextSection) {
            newStates[nextSection.id] = true;
          }
          
          return newStates;
        });
      }
    }
  };

  const toggleSection = (sectionId: string) => {
    setSectionStates(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const calculateProgress = () => {
    const totalSteps = tutorial.sections.reduce((acc, section) => acc + section.steps.length, 0);
    const completedSteps = progress.completedSteps.length;
    return totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft size={16} className="mr-2" />
          Back to Tutorials
        </Button>
        
        {tutorial.coverImage && (
          <div className="aspect-video bg-muted rounded-lg overflow-hidden">
            <img
              src={tutorial.coverImage}
              alt={tutorial.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        
        <div className="space-y-3">
          <h1 className="text-3xl font-bold">{tutorial.title}</h1>
          <div 
            className="text-muted-foreground text-lg prose prose-lg max-w-none"
            dangerouslySetInnerHTML={createSafeHTML(tutorial.shortDescription)}
          />
          
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Clock size={16} />
              {tutorial.sections.length} sections
            </div>
            <div className="text-sm font-medium">
              Progress: {calculateProgress()}%
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {tutorial.tags.map((tag) => (
              <Badge key={tag} variant="outline">
                {tag}
              </Badge>
            ))}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-muted rounded-full h-2">
        <div 
          className="bg-primary h-2 rounded-full transition-all duration-300"
          style={{ width: `${calculateProgress()}%` }}
        />
      </div>

      {/* Bill of Materials */}
      {sortedBom.length > 0 && (
        <Collapsible defaultOpen>
          <Card className="p-6">
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between p-0 h-auto">
                <h2 className="text-xl font-semibold">Bill of Materials ({sortedBom.length} items)</h2>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4">
              <div className="space-y-3">
                {sortedBom.map((item) => (
                  <div key={item.id} className="flex items-start justify-between p-3 border rounded-lg">
                    <div className="space-y-1 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{item.name}</span>
                        <Badge variant="secondary" className="text-xs">
                          Qty: {item.quantity}
                        </Badge>
                        {item.categories && item.categories.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {item.categories.map((category, index) => (
                              <Badge key={index} variant="outline" className="text-xs">
                                {category}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      {item.description && (
                        <div 
                          className="text-sm text-muted-foreground prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={createSafeHTML(item.description)}
                        />
                      )}
                    </div>
                    {item.links && item.links.length > 0 && (
                      <div className="flex flex-wrap gap-2 ml-4">
                        {item.links.map((link) => (
                          <Button 
                            key={link.id}
                            variant="outline" 
                            size="sm" 
                            asChild
                          >
                            <a href={link.url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink size={14} className="mr-1" />
                              {link.name || "Link"}
                            </a>
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      {/* Tutorial Sections */}
      <div className="space-y-4">
        {tutorial.sections.map((section, sectionIndex) => {
          const sectionProgress = section.steps.length > 0 
            ? section.steps.filter(step => progress.completedSteps.includes(step.id)).length / section.steps.length * 100
            : 0;
          
          return (
            <Collapsible
              key={section.id}
              open={sectionStates[section.id]}
              onOpenChange={() => toggleSection(section.id)}
            >
              <Card className="overflow-hidden">
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full justify-between p-6 h-auto">
                    <div className="flex items-center gap-3">
                      {sectionStates[section.id] ? (
                        <ChevronDown className="h-5 w-5" />
                      ) : (
                        <ChevronRight className="h-5 w-5" />
                      )}
                      <div className="text-left">
                        <h3 className="text-lg font-semibold">
                          {sectionIndex + 1}. {section.title}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {section.steps.length} steps • {Math.round(sectionProgress)}% complete
                        </p>
                      </div>
                    </div>
                    <div className="w-16 h-2 bg-muted rounded-full">
                      <div 
                        className="h-2 bg-primary rounded-full transition-all duration-300"
                        style={{ width: `${sectionProgress}%` }}
                      />
                    </div>
                  </Button>
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  <div className="px-6 pb-6 space-y-4">
                    {section.steps.map((step, stepIndex) => {
                      const isCompleted = progress.completedSteps.includes(step.id);
                      
                      return (
                        <div key={step.id} className="flex gap-4 p-4 border rounded-lg">
                          <Checkbox
                            checked={isCompleted}
                            onCheckedChange={() => toggleStep(step.id)}
                            className="mt-1"
                          />
                          <div className="flex-1 space-y-3">
                            <div className="space-y-2">
                              <h4 className="font-medium">Step {stepIndex + 1}</h4>
                              <div 
                                className={`text-sm prose prose-sm max-w-none ${isCompleted ? 'line-through text-muted-foreground' : ''}`}
                                dangerouslySetInnerHTML={createSafeHTML(step.text)}
                              />
                            </div>
                            
                            {step.images.length > 0 && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {step.images.map((image, imageIndex) => (
                                  <div key={imageIndex} className="aspect-video bg-muted rounded-lg overflow-hidden">
                                    <img
                                      src={image}
                                      alt={`Step ${stepIndex + 1} - Image ${imageIndex + 1}`}
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          );
        })}
      </div>
    </div>
  );
};

export default TutorialViewer;
